/* eslint-disable consistent-return */
import { useState } from 'react';
import axios from 'axios';
import { useAuth } from './useAuth';

const useAxios = (contentType = 'application/json') => {
  const { logout } = useAuth();
  const [apiLoading, setLoading] = useState(false);
  const [apiError, setError] = useState(null);

  const instance = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    headers: {
      'Content-Type': contentType,
      timeout: 5000,
    },
    // .. other options
  });

  instance.interceptors.request.use(async (config: any) => {
    config.headers.Authorization = undefined;

    if (
      config.method === 'post'
      || config.method === 'put'
      || config.method === 'patch'
      || config.method === 'delete'
    ) {
      const payload = config.data;
      if (payload) {
        config.data = payload;
      }
    }

    const token = localStorage.getItem('authToken');
    config.headers.Authorization = token ? `Bearer ${token}` : '';

    return config;
  });

  let sessionExpired = false;

  instance.interceptors.response.use(
    (response: any) => {
      if (response.data?.errors && response.data?.errors.length > 0 && (response.data?.errors[0] === 'ERROR: Token Expired' || response.data?.errors[0] === 'ERROR: Token mismatch in \'directory_token_validation\'')) {
        sessionExpired = true;
        
        logout();
        return response;
      } else {
        sessionExpired = false;
        return response;
      }
    },
    (error) => {
      const errorCode = error?.response?.data?.error?.errorCode;
      return Promise.reject(error);
    }
  );

  const get = async (url: any) => {
    setLoading(true);
    setError(null);

    try {
      const response = await instance.get(url).then((res: any) => {
        const { status } = res;
        if (status && [200, 204].includes(status)) {
          return res;
        }
      });

      return response;
    } catch (err: any) {
      setError(err.response ? err.response.data : err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const post = async (url: any, data: any) => {
    setLoading(true);
    setError(null);

    try {
      const response = await instance.post(url, data).then((res: any) => {
        const { status } = res;
        if (status && [200, 204].includes(status)) {
          return res;
        }
      });

      return response;
    } catch (err: any) {
      setError(err.response ? err.response.data : err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const put = async (url: any, data: any) => {
    setLoading(false);
    setError(null);

    try {
      const response = await instance.put(url, data).then((res: any) => {
        const { status } = res;
        if (status && [200, 204].includes(status)) {
          return res;
        }
      });

      return response;
    } catch (err: any) {
      setError(err.response ? err.response.data : err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const patch = async (url: any, data: any) => {
    setLoading(false);
    setError(null);

    try {
      const response = await instance.patch(url, data).then((res: any) => {
        const { status } = res;
        if (status && [200, 204].includes(status)) {
          return res;
        }
      });

      return response;
    } catch (err: any) {
      setError(err.response ? err.response.data : err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  const deleteRequest = async (url: any) => {
    setLoading(false);
    setError(null);

    try {
      const response = await instance.delete(url).then((res: any) => {
        const { status } = res;
        if (status && [200, 204].includes(status)) {
          return res;
        }
      });

      return response;
    } catch (err: any) {
      setError(err.response ? err.response.data : err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { get, post, put, deleteRequest, patch, apiLoading, apiError };
};

export default useAxios;
